import { Auth } from 'aws-amplify';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const LOG_FLUSH_INTERVAL = 5000;

const logBuffer: Set<string> = new Set();
let logFlushTimeout: NodeJS.Timeout | null = null;

let apiUrl = '';

let sessionId: string;
let sequenceNumber = 0;

const UUID_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Normalize a URL path for use as a low-cardinality EMF dimension.
 * Strips UUIDs and URL-encoded composite keys so that paths like
 * `/marketplace/deal/OFFERING%233fe5...%23BUYER%23e18c...` collapse to
 * `/marketplace/deal/:compositeId`.
 */
const normalizePath = (path: string): string => {
  let decoded = path;
  try {
    decoded = decodeURIComponent(path);
  } catch {
    // malformed URI -- use raw path
  }

  let normalized = decoded.replace(UUID_REGEX, ':id');

  if (normalized.includes('OFFERING#') || normalized.includes('BUYER#')) {
    normalized = normalized.replace(/\/[A-Z]+#[^/]+/, '/:compositeId');
  }

  return normalized;
};

export const initializeLogger = (url: string) => {
  apiUrl = url;

  sessionId = sessionStorage.getItem('app_session_id') || uuidv4();
  sessionStorage.setItem('app_session_id', sessionId);

  sequenceNumber = 0;

  logEmfMetric('SessionStart', {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    url: window.location.pathname,
  });

  interceptConsoleLogs();
  setupErrorCapture();
  setupEnhancedUserInteractionLogging();
  logPerformanceStats();
};

const getToken = async (): Promise<string> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch {
    return '';
  }
};

export const sendLogs = async (logs: string[] | string, token: string) => {
  if (!Array.isArray(logs)) {
    logs = [logs];
  }
  if (!apiUrl || !token) {
    return;
  }

  logs.forEach((log) => logBuffer.add(log));

  if (!logFlushTimeout) {
    logFlushTimeout = setTimeout(async () => {
      const logsToSend = Array.from(logBuffer);
      logBuffer.clear();
      logFlushTimeout = null;

      if (logsToSend.length > 0) {
        try {
          await axios.post(
            apiUrl,
            { logs: logsToSend },
            {
              headers: {
                Authorization: token,
              },
            },
          );
        } catch {
          // Silently drop — logging failures must not trigger unhandledrejection
        }
      }
    }, LOG_FLUSH_INTERVAL);
  }
};

const sendEmfLog = (emfLog: Record<string, unknown>) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[EMF]', emfLog);
    return;
  }
  const serialized = JSON.stringify(emfLog);
  getToken()
    .then((token) => sendLogs(serialized, token))
    .catch(() => {});
};

const interceptConsoleLogs = () => {
  const originalError = console.error;

  console.error = (...args) => {
    originalError(...args);

    if (process.env.NODE_ENV !== 'development') {
      const message = args
        .map((arg) => {
          try {
            return typeof arg === 'string' ? arg : JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        })
        .join(' ');

      logEmfMetric(
        'ClientError',
        {
          errorType: 'console.error',
          message: message.substring(0, 1000),
          page: window.location.pathname,
          timestamp: new Date().toISOString(),
        },
        { count: { value: 1, unit: 'Count' } },
      );
    }
  };
};

const setupErrorCapture = () => {
  window.addEventListener('error', (event) => {
    logEmfMetric(
      'ClientError',
      {
        errorType: 'uncaught',
        message: event.message,
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack?.substring(0, 500),
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      },
      { count: { value: 1, unit: 'Count' } },
    );
  });

  window.addEventListener('unhandledrejection', (event) => {
    logEmfMetric(
      'ClientError',
      {
        errorType: 'unhandledrejection',
        message: String(event.reason),
        stack: event.reason?.stack?.substring(0, 500),
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      },
      { count: { value: 1, unit: 'Count' } },
    );
  });
};

/**
 * Emit a CloudWatch Embedded Metric Format log entry.
 *
 * Dimension names MUST exactly match top-level keys in the emitted JSON.
 * Only low-cardinality values belong in dimensions (LogType, Page).
 * High-cardinality identifiers (sessionId, userId) go as properties —
 * they're queryable via CloudWatch Logs Insights but don't create
 * separate metric streams at ~$0.30/metric/month.
 */
const logEmfMetric = (
  logType: string,
  properties: Record<string, unknown>,
  metrics?: Record<string, { value: number; unit: string }>,
) => {
  const timestamp = Date.now();
  const page = normalizePath(window.location.pathname);

  const emfLog: Record<string, unknown> = {
    _aws: {
      Timestamp: timestamp,
      CloudWatchMetrics: [
        {
          Namespace: 'ORSNN/Application',
          Dimensions: [['LogType', 'Page']],
          Metrics: metrics
            ? Object.keys(metrics).map((name) => ({
                Name: name,
                Unit: metrics[name].unit,
              }))
            : [{ Name: 'count', Unit: 'Count' }],
        },
      ],
    },
    LogType: logType,
    Page: page,
    sessionId,
    sequence: ++sequenceNumber,
    ...properties,
  };

  if (metrics) {
    for (const [key, metric] of Object.entries(metrics)) {
      emfLog[key] = metric.value;
    }
  } else {
    emfLog['count'] = 1;
  }

  sendEmfLog(emfLog);
};

const setupEnhancedUserInteractionLogging = () => {
  document.addEventListener(
    'click',
    (e: MouseEvent) => {
      if (e.target instanceof Element) {
        const actionElement = e.target.closest(
          'button, a, [role="button"], input[type="submit"], input[type="button"]',
        );

        logEmfMetric(
          'UserInteraction',
          {
            type: 'click',
            elementType: e.target.tagName || 'unknown',
            elementId: e.target.id || 'none',
            elementClass: e.target.className || 'none',
            elementAriaLabel: e.target.getAttribute('aria-label') || 'none',
            actionElement: actionElement
              ? {
                  type: actionElement.tagName,
                  id: actionElement.id || 'none',
                  text: actionElement.textContent?.substring(0, 50).trim() || 'none',
                }
              : undefined,
            x: e.clientX,
            y: e.clientY,
            page: window.location.pathname,
            timestamp: new Date().toISOString(),
          },
          { count: { value: 1, unit: 'Count' } },
        );
      } else {
        logEmfMetric(
          'UserInteraction',
          {
            type: 'click',
            elementType: 'unknown',
            e: JSON.stringify(e),
            timestamp: new Date().toISOString(),
            page: window.location.pathname,
          },
          { count: { value: 1, unit: 'Count' } },
        );
      }

      const componentEl =
        e.target instanceof Element ? e.target.closest('[data-ui-component]') : null;

      if (componentEl) {
        logEmfMetric('ComponentInteraction', {
          component: componentEl.getAttribute('data-ui-component'),
          action: componentEl.getAttribute('data-ui-action') || 'click',
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
        });
      }
    },
    { passive: true },
  );

  window.addEventListener(
    'popstate',
    () => {
      logEmfMetric('UserInteraction', {
        type: 'navigation',
        method: 'popstate',
        path: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    },
    { passive: true },
  );

  document.addEventListener(
    'submit',
    (e) => {
      if (e.target instanceof HTMLFormElement) {
        logEmfMetric('UserInteraction', {
          type: 'form_submit',
          formId: e.target.id || 'none',
          formAction: e.target.action?.split('?')[0] || 'none',
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
        });
      }
    },
    { passive: false },
  );

  document.addEventListener(
    'focus',
    (e) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLSelectElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        logEmfMetric('UserInteraction', {
          type: 'input_focus',
          elementType: e.target.tagName,
          inputType:
            e.target instanceof HTMLInputElement ? e.target.type : e.target.tagName.toLowerCase(),
          elementId: e.target.id || 'none',
          elementName: e.target.name || 'none',
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
        });
      }
    },
    { passive: true, capture: true },
  );

  document.addEventListener(
    'scroll',
    () => {
      if (!window.scrollLoggingThrottled) {
        window.scrollLoggingThrottled = true;
        setTimeout(() => {
          window.scrollLoggingThrottled = false;
        }, 1000);

        logEmfMetric('UserInteraction', {
          type: 'scroll',
          scrollY: window.scrollY,
          scrollX: window.scrollX,
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
        });
      }
    },
    { passive: true },
  );
};

type PerformanceStats = {
  timeToFirstByte: number;
  domContentLoaded: number;
  pageLoadTime: number;
  firstPaint: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
  navigationType: string;
};

let perfDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let lastLoggedNavPath = '';

const logPerformanceStats = () => {
  const logStats = () => {
    const capturedPath = window.location.pathname;
    if (capturedPath === lastLoggedNavPath) return;
    lastLoggedNavPath = capturedPath;

    window.requestAnimationFrame(() => {
      const navigationEntry = performance.getEntriesByType(
        'navigation',
      )[0] as PerformanceNavigationTiming;
      if (!navigationEntry) return;

      const performanceData: PerformanceStats = {
        timeToFirstByte: navigationEntry.responseStart - navigationEntry.requestStart,
        domContentLoaded: navigationEntry.domContentLoadedEventEnd - navigationEntry.startTime,
        pageLoadTime: navigationEntry.loadEventEnd - navigationEntry.startTime,
        firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime || 0,
        firstContentfulPaint:
          performance.getEntriesByName('first-contentful-paint')[0]?.startTime || 0,
        largestContentfulPaint:
          (performance.getEntriesByType('largest-contentful-paint')[0] as PerformanceEntry)
            ?.startTime || 0,
        navigationType: navigationEntry.type,
      };

      const performanceMetrics = {
        timeToFirstByte: {
          value: performanceData.timeToFirstByte,
          unit: 'Milliseconds',
        },
        domContentLoaded: {
          value: performanceData.domContentLoaded,
          unit: 'Milliseconds',
        },
        pageLoadTime: {
          value: performanceData.pageLoadTime,
          unit: 'Milliseconds',
        },
        firstPaint: { value: performanceData.firstPaint, unit: 'Milliseconds' },
        firstContentfulPaint: {
          value: performanceData.firstContentfulPaint,
          unit: 'Milliseconds',
        },
        largestContentfulPaint: {
          value: performanceData.largestContentfulPaint,
          unit: 'Milliseconds',
        },
      };

      logEmfMetric(
        'PerformanceStats',
        {
          page: capturedPath,
          timestamp: new Date().toISOString(),
          navigationType: performanceData.navigationType,
        },
        performanceMetrics,
      );

      logPageView(capturedPath);
    });
  };

  const debouncedLogStats = () => {
    if (perfDebounceTimer) clearTimeout(perfDebounceTimer);
    perfDebounceTimer = setTimeout(logStats, 500);
  };

  if (document.readyState === 'complete') {
    logStats();
  } else {
    window.addEventListener('load', logStats);
  }

  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    originalPushState.apply(this, args);
    lastLoggedNavPath = '';
    debouncedLogStats();

    logEmfMetric('UserInteraction', {
      type: 'navigation',
      method: 'pushState',
      path: typeof args[2] === 'string' ? args[2] : window.location.pathname,
      timestamp: new Date().toISOString(),
    });
  };

  const originalReplaceState = history.replaceState;
  history.replaceState = function (...args) {
    originalReplaceState.apply(this, args);
    lastLoggedNavPath = '';
    debouncedLogStats();

    logEmfMetric('UserInteraction', {
      type: 'navigation',
      method: 'replaceState',
      path: typeof args[2] === 'string' ? args[2] : window.location.pathname,
      timestamp: new Date().toISOString(),
    });
  };

  window.addEventListener('popstate', () => {
    lastLoggedNavPath = '';
    debouncedLogStats();
  });
};

const logPageView = (path: string) => {
  logEmfMetric(
    'PageView',
    {
      path,
      timestamp: new Date().toISOString(),
    },
    { count: { value: 1, unit: 'Count' } },
  );
};

export const logLatency = (operation: string, latencyMs: number) => {
  logEmfMetric(
    'Latency',
    {
      operation,
      timestamp: new Date().toISOString(),
    },
    { latencyMs: { value: latencyMs, unit: 'Milliseconds' } },
  );
};

export const getCurrentSessionId = (): string => sessionId;

export const getCurrentSequenceNumber = (): number => sequenceNumber;

declare global {
  interface Window {
    scrollLoggingThrottled?: boolean;
  }
}
